import { useMemo, useState } from 'react';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import {
  type ResourceSet,
  useResourceSets,
} from '../../../IAP/hooks/resourceSetHooks';
import { AddRequestedResource } from './AddRequestedResource';
import { ResourceRequestItem } from 'components/IAP/hooks/incidentResourceRequestHooks';
import ResourceTabCard from './ResourceTabCard';
import { resourceRequestItemToResourceRow } from './ResourceRequestItemToResourceRow';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';

type DropdownResource =
  | ResourceSet
  | (ResourceRequestItem & { incidents?: UUID[] });

type SearchableOption = OptionType & {
  searchText?: string;
};

export const CustomResourcesDropdown = ({
  arrIndex,
  logisticsItems,
  setLogisticsItems,
  reqCount,
  isDisabled,
}: {
  arrIndex?: number;
  logisticsItems: ResourceRequestItem[];
  setLogisticsItems: React.Dispatch<
    React.SetStateAction<ResourceRequestItem[][]>
  >;
  reqCount?: Record<string, number>;
  isDisabled?: boolean;
}) => {
  const [showAdd, setShowAdd] = useState(false);
  const [customResources, setCustomResources] = useState<ResourceRequestItem[]>(
    []
  );
  const { data: resourceSets } = useResourceSets();
  const { data: incidents } = useAllIncidents();
  const createResourceValue = 'createNewResource';

  const resourceRowColumnWidths = ['20%', '20%', '10%', '15%', '20%', '5%'];
  const padding = { paddingLeft: '1.75rem' };
  const resourceRowStyle = {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: resourceRowColumnWidths.join(' '),
    gap: '5px',
  } as const;

  const headerRowStyle = {
    ...resourceRowStyle,
    ...padding,
  } as const;

  const headerLabel = (
    <div style={headerRowStyle}>
      <div key={1}>Name</div>
      <div key={2}>Kind</div>
      <div key={3}>Type</div>
      <div key={4}>In Incident</div>
      <div key={5}>In Request</div>
      <div key={6}>Quantity</div>
    </div>
  );

  const allResources = useMemo(() => {
    return [...(customResources || []), ...(resourceSets || [])];
  }, [resourceSets, customResources]);

  const styledLabel = (rs: DropdownResource) => (
    <div style={resourceRowStyle}>
      <div>{rs.name}</div>
      <div>{rs.kind}</div>
      <div style={{ fontWeight: 'bold', color: 'var(--brand-primary)' }}>
        {rs.type}
      </div>
      {rs?.incidents && rs?.incidents?.length > 0 ? (
        <div style={{ color: '#dc6803' }}>
          In Use by{' '}
          {incidents?.find((inc) => inc.id === rs.incidents?.[0])?.name ||
            'Unknown'}
        </div>
      ) : (
        <div style={{ color: '#12b76a' }}>Available</div>
      )}
      {reqCount?.[rs.resource_set_id || ''] ? (
        <div
          style={{ color: '#dc6803' }}
        >{`Requested ${reqCount?.[rs.resource_set_id || '']} times`}</div>
      ) : (
        <div style={{ color: '#12b76a' }}>Not Requested</div>
      )}
      <div>QTY:{rs.quantity}</div>
    </div>
  );

  const filteredResourceSets = (): SearchableOption[] => {
    return [
      ...(customResources?.map((rs) => ({
        value: rs.id || '',
        label: styledLabel(rs),
        searchText: rs.name,
      })) || []),
      ...(resourceSets?.map((rs) => ({
        value: rs.id || '',
        label: styledLabel(rs),
        searchText: rs.name,
      })) || []),
    ];
  };

  const unstyled = useMemo(() => {
    return [
      ...(customResources?.map((rs) => ({
        value: rs.id || '',
        label: rs.name,
      })) || []),
      ...(resourceSets?.map((rs) => ({
        value: rs.id || '',
        label: rs.name,
      })) || []),
    ];
  }, [resourceSets, customResources]);

  const groupedOptions = [
    {
      label: 'addResource',
      options: [{ value: createResourceValue, label: 'Add Resource' }],
    },
    {
      label: 'Resources',
      options: filteredResourceSets(),
    },
  ];

  return (
    <>
      <PratusSelect
        options={groupedOptions}
        formatGroupLabel={(group) =>
          group.label === 'addResource' ? (
            <div style={{ ...padding }}>Custom Resource</div>
          ) : (
            headerLabel
          )
        }
        value={unstyled.filter((option) =>
          logisticsItems?.some((item) => item.id === option.value)
        )}
        filterOption={(option, rawInput) => {
          const optionData = option.data as SearchableOption;
          if (rawInput === '' || optionData.label === 'addResource') {
            return true;
          }
          return (
            optionData.searchText
              ?.toLowerCase()
              .includes(rawInput.toLowerCase()) ?? false
          );
        }}
        onChange={(value) => {
          const newValues =
            (value as { value: string; label: string }[] | null)?.map(
              (v) => v.value
            ) ?? [];
          if (newValues?.some((v: string) => v === createResourceValue)) {
            setShowAdd(true);
          }
          const selectedResources = newValues
            ?.map((id: string) => allResources?.find((rs) => rs.id === id))
            .filter(Boolean);
          setLogisticsItems((prev) => {
            const newItems = [...prev];
            newItems[arrIndex || 0] =
              selectedResources as ResourceRequestItem[];
            return newItems;
          });
        }}
        placeholder="Attach Resources to Request"
        isMulti
        isDisabled={isDisabled}
      />
      {showAdd && (
        <AddRequestedResource
          setCustomResources={setCustomResources}
          setShowAdd={setShowAdd}
          showOwningOrg={true}
        />
      )}
      {logisticsItems.map((item, idx) => (
        <ResourceTabCard
          key={idx}
          row={resourceRequestItemToResourceRow(item)}
          groupMembers={[]}
          setShowCheckInDrawer={() => {}}
          viewOnly={true}
        />
      ))}
    </>
  );
};
